/**
 * Contingency matter field options service
 * Fetches case statuses and marketing sources from API with caching
 */
import axios from 'axios';

// API endpoints (base URL is set by axios interceptor in main.js)
const CASE_STATUSES_URL = '/matters/case-statuses';
const MARKETING_SOURCES_URL = '/matters/marketing-sources';

// Cache for API responses (singleton pattern)
let caseStatusCache = null;
let marketingSourceCache = null;
let caseStatusPromise = null;
let marketingSourcePromise = null;

/**
 * Fetch case status options from API (cached)
 * @returns {Promise<Array<{value: string, label: string}>>}
 */
export async function fetchCaseStatusOptions() {
  if (caseStatusCache) {
    return caseStatusCache;
  }

  // Prevent duplicate requests while one is in flight
  if (!caseStatusPromise) {
    caseStatusPromise = axios.get(CASE_STATUSES_URL).then(response => {
      caseStatusCache = response.data;
      caseStatusPromise = null;
      return caseStatusCache;
    }).catch(error => {
      caseStatusPromise = null;
      throw error;
    });
  }
  return caseStatusPromise;
}

/**
 * Fetch marketing source options from API (cached)
 * @returns {Promise<Array<{value: string, label: string}>>}
 */
export async function fetchMarketingSourceOptions() {
  if (marketingSourceCache) {
    return marketingSourceCache;
  }

  // Prevent duplicate requests while one is in flight
  if (!marketingSourcePromise) {
    marketingSourcePromise = axios.get(MARKETING_SOURCES_URL).then(response => {
      marketingSourceCache = response.data;
      marketingSourcePromise = null;
      return marketingSourceCache;
    }).catch(error => {
      marketingSourcePromise = null;
      throw error;
    });
  }
  return marketingSourcePromise;
}

/**
 * Fetch both options in parallel (for initial load)
 * @returns {Promise<{caseStatuses: Array, marketingSources: Array}>}
 */
export async function fetchContingencyOptions() {
  const [caseStatuses, marketingSources] = await Promise.all([fetchCaseStatusOptions(), fetchMarketingSourceOptions()]);
  return {
    caseStatuses,
    marketingSources
  };
}

/**
 * Get cached case status label (synchronous, returns null if not cached)
 * @param {string} value - The backend enum value
 * @returns {string|null}
 */
export function getCaseStatusLabel(value) {
  if (!value || !caseStatusCache) return value || null;
  const option = caseStatusCache.find(o => o.value === value);
  return option ? option.label : value;
}

/**
 * Get cached marketing source label (synchronous, returns null if not cached)
 * @param {string} value - The backend enum value
 * @returns {string|null}
 */
export function getMarketingSourceLabel(value) {
  if (!value || !marketingSourceCache) return value || null;
  const option = marketingSourceCache.find(o => o.value === value);
  return option ? option.label : value;
}

/**
 * Clear cache (useful for testing or force refresh)
 */
export function clearContingencyOptionsCache() {
  caseStatusCache = null;
  marketingSourceCache = null;
  caseStatusPromise = null;
  marketingSourcePromise = null;
}